import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import {
  ProtocolGradeResponse,
  ProtocolResponse,
  ProtocolGradeRequest,
  RecognitionProtocolResponse,
} from "../../models/dtos";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import Error from "../../components/common/Error";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useRef, useState, useEffect, memo } from "react";
import { InputText } from "primereact/inputtext";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { Formik } from "formik";
import { formatGrade } from "../../helpers/gradeHelpers";

interface ProtocolGradeSubjectsListProps {
  protocol: ProtocolResponse | RecognitionProtocolResponse;
}

interface EditableProtocolGrade extends ProtocolGradeResponse {
  gradeInput: string;
  thesisTitleInput: string;
}

// Memoized grade cell component
const GradeCell = memo(
  ({
    rowData,
    options,
    editMode,
    handleGradeKeyDown,
    handleGradeInputChange,
    handleGradeInputBlur,
  }: {
    rowData: EditableProtocolGrade;
    options: any;
    editMode: boolean;
    handleGradeKeyDown: (
      e: React.KeyboardEvent<HTMLInputElement>,
      options: any,
      rowData: EditableProtocolGrade,
    ) => void;
    handleGradeInputChange: (studentGradeId: number, value: string) => void;
    handleGradeInputBlur: (studentGradeId: number) => void;
  }) => {
    return editMode ? (
      <InputText
        value={rowData.gradeInput}
        onClick={(e) => (e.target as HTMLInputElement).select()}
        onKeyDown={(e) => handleGradeKeyDown(e, options, rowData)}
        onChange={(e) => handleGradeInputChange(rowData.studentGradeId, e.target.value)}
        onBlur={() => handleGradeInputBlur(rowData.studentGradeId)}
        className="w-full protocol-grade-input"
      />
    ) : (
      formatGrade(rowData.grade)
    );
  },
);

const ProtocolGradeSubjectsList = ({ protocol }: ProtocolGradeSubjectsListProps) => {
  const toast = useRef<Toast>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingGrades, setEditingGrades] = useState<EditableProtocolGrade[]>([]);
  const [originalGrades, setOriginalGrades] = useState<ProtocolGradeResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: grades,
    error: gradesError,
    isLoading: gradesLoading,
    refetch: refetchGrades,
  } = protocolsApi.useGetProtocolGradesQuery(protocol.protocolId!);

  const [removeSubject] = protocolsApi.useRemoveProtocolSubjectMutation();

  const [updateSubjectGrade] = protocolsApi.useAdminUpdateStudentGradeMutation();

  useEffect(() => {
    if (grades?.data) {
      const editable = grades.data.map((grade) => ({
        ...grade,
        gradeInput: formatGrade(grade.grade),
        thesisTitleInput: grade.thesisTitle || "",
      }));
      setEditingGrades(editable);
      setOriginalGrades(structuredClone(grades.data));
    }
  }, [grades?.data]);

  const handleDelete = (semester: number, subjectId: number) => {
    confirmDialog({
      message: "Сигурни ли сте, че искате да премахнете дисциплината от протокола?",
      header: "Потвърждение",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        removeSubject({ protocolId: protocol.protocolId!, semester: semester, subjectId: subjectId })
          .unwrap()
          .then((result) => {
            if (result && "data" in result) {
              if (result.data === true) {
                toast.current?.show({
                  severity: "success",
                  summary: "Успешно",
                  detail: "Дисциплината беше премахната от протокола",
                });
              } else {
                toast.current?.show({
                  severity: "error",
                  summary: "Грешка",
                  detail: result.message,
                });
              }
            }
          });
      },
    });
  };

  const handleFieldEdit = (
    subjectId: number,
    updatedFields: Partial<ProtocolGradeResponse & { gradeInput?: string; thesisTitleInput?: string }>,
  ) => {
    setEditingGrades((prev) => prev.map((g) => (g.subjectId === subjectId ? { ...g, ...updatedFields } : g)));
  };

  const isValidGrade = (value: string): boolean => {
    const parsedValue = parseFloat(value);
    const validWholeGrades = [0, 2, 3, 4, 5, 6];
    const validDecimalGrades = [3.5, 4.5, 5.5];
    return validWholeGrades.includes(parsedValue) || validDecimalGrades.includes(parsedValue);
  };

  const handleGradeInputChange = (studentGradeId: number, value: string) => {
    if (!/^\d*\.?\d*$/.test(value)) return;
    setEditingGrades((prev) => prev.map((g) => (g.studentGradeId === studentGradeId ? { ...g, gradeInput: value } : g)));
  };

  const handleGradeInputBlur = (studentGradeId: number) => {
    const item = editingGrades.find((g) => g.studentGradeId === studentGradeId);
    if (!item) return;
    const { gradeInput } = item;
    if (isValidGrade(gradeInput)) {
      const parsedGrade = parseFloat(gradeInput);
      handleFieldEdit(studentGradeId, { grade: parsedGrade, gradeInput: gradeInput });
    } else {
      // Reset to the current grade value if input is invalid
      setEditingGrades((prev) =>
        prev.map((g) => (g.studentGradeId === studentGradeId ? { ...g, gradeInput: formatGrade(g.grade) } : g)),
      );
    }
  };

  const handleUpdateDateEdit = (rowData: ProtocolGradeResponse, newValue: Date | null) => {
    handleFieldEdit(rowData.studentGradeId, { updateDate: newValue });
  };

  const handleGradeKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    options: any,
    rowData: ProtocolGradeResponse,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleGradeInputBlur(rowData.studentGradeId);
      const inputs = document.querySelectorAll(".protocol-grade-input") as NodeListOf<HTMLInputElement>;
      const nextInput = inputs[options.rowIndex + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
  };

  const hasChanges = () => {
    for (let i = 0; i < editingGrades.length; i++) {
      const current = editingGrades[i];
      const original = originalGrades.find((g) => g.studentGradeId === current.studentGradeId);

      if (!original) continue;

      // Check if gradeInput is different from original grade (handles pending grade changes)
      const currentGradeValue = isValidGrade(current.gradeInput) ? parseFloat(current.gradeInput) : current.grade;
      const originalGradeValue = original.grade;

      if (
        currentGradeValue !== originalGradeValue ||
        current.studentGradeTypeId !== original.studentGradeTypeId ||
        current.attestation !== original.attestation ||
        current.thesisTitle !== original.thesisTitle ||
        current.thesisTitleInput !== (original.thesisTitle || "") ||
        JSON.stringify(current.studentGradeComponents) !== JSON.stringify(original.studentGradeComponents) ||
        current.updateDate?.toString() !== original.updateDate?.toString()
      ) {
        return true;
      }
    }
    return false;
  };

  const saveAllChanges = async () => {
    if (!hasChanges()) {
      setEditMode(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const changedGrades = editingGrades.filter((current) => {
        const original = originalGrades.find((g) => g.studentGradeId === current.studentGradeId);
        if (!original) return false;

        // Check if gradeInput is different from original grade (handles pending grade changes)
        const currentGradeValue = isValidGrade(current.gradeInput) ? parseFloat(current.gradeInput) : current.grade;
        const originalGradeValue = original.grade;

        return (
          currentGradeValue !== originalGradeValue ||
          current.studentGradeTypeId !== original.studentGradeTypeId ||
          current.attestation !== original.attestation ||
          current.thesisTitle !== original.thesisTitle ||
          current.thesisTitleInput !== (original.thesisTitle || "") ||
          JSON.stringify(current.studentGradeComponents) !== JSON.stringify(original.studentGradeComponents) ||
          current.updateDate?.toString() !== original.updateDate?.toString()
        );
      });

      // Process in sequential order
      for (const grade of changedGrades) {
        // Use gradeInput if it's a valid grade, otherwise use the existing grade value
        const finalGradeValue = isValidGrade(grade.gradeInput) ? parseFloat(grade.gradeInput) : grade.grade;
        const finalThesisTitle = grade.thesisTitleInput || grade.thesisTitle || null;
        
        const gradeRequest: ProtocolGradeRequest = {
          studentGradeId: grade.studentGradeId,
          studentId: grade.studentId,
          protocolId: grade.protocolId,
          grade: finalGradeValue,
          studentGradeTypeId: grade.studentGradeTypeId,
          attestation: grade.attestation,
          studentGradeComponents: grade.studentGradeComponents,
          updateDate: grade.updateDate || null,
          thesisTitle: finalThesisTitle,
          subjectId: grade.subjectId || null,
          semester: grade.semester || null,
          subjectName: grade.subjectName || null,
        };

        await updateSubjectGrade(gradeRequest).unwrap();
      }

      toast.current?.show({
        severity: "success",
        summary: "Успешно",
        detail: `${changedGrades.length} ${changedGrades.length === 1 ? "оценка беше" : "оценки бяха"} успешно обновени`,
      });

      refetchGrades();
      setEditMode(false);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Грешка",
        detail: "Възникна грешка при запазването на оценките",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (gradesLoading) return <Skeleton className="w-full h-20rem" />;
  if (gradesError) return <Error error={gradesError} />;

  return (
    <>
      <Toast ref={toast} />
      <Card className="shadow-4">
        <div className="flex justify-content-between align-items-center">
          <h3>Дисциплини</h3>
          {editMode ? (
            <div className="flex gap-2">
              <Button
                icon="pi pi-times"
                rounded
                severity="secondary"
                onClick={() => {
                  // Reset to original data
                  if (grades?.data) {
                    const editable = grades.data.map((grade) => ({
                      ...grade,
                      gradeInput: formatGrade(grade.grade),
                      thesisTitleInput: grade.thesisTitle || "",
                    }));
                    setEditingGrades(editable);
                  }
                  setEditMode(false);
                }}
                disabled={isSubmitting}
              />
              <Button
                icon="pi pi-check"
                rounded
                severity="success"
                onClick={saveAllChanges}
                loading={isSubmitting}
                disabled={!hasChanges()}
              />
            </div>
          ) : (
            <Button icon="pi pi-pencil" rounded onClick={() => setEditMode(true)} />
          )}
        </div>
        {protocol.examTypeId === 18 ? (
          <DataTable value={editingGrades} stripedRows showGridlines>
            <Column field="subjectName" header="Дисциплина" sortable />
            <Column field="semester" header="Семестър" sortable />
            <Column
              field="grade"
              header="Оценка"
              style={{ width: "80px" }}
              body={(rowData, options) => (
                <GradeCell
                  rowData={rowData as EditableProtocolGrade}
                  options={options}
                  editMode={editMode}
                  handleGradeKeyDown={handleGradeKeyDown}
                  handleGradeInputChange={handleGradeInputChange}
                  handleGradeInputBlur={handleGradeInputBlur}
                />
              )}
            />
            <Column
              field="updateDate"
              header="Дата"
              style={{ width: "200px" }}
              body={(rowData) =>
                editMode ? (
                  <Formik
                    initialValues={{ updateDate: rowData.updateDate ? new Date(rowData.updateDate) : null }}
                    onSubmit={() => {}}
                  >
                    <CustomDatepicker
                      id="updateDate"
                      label=""
                      showIcon={false}
                      onChange={(e: any) => handleUpdateDateEdit(rowData, e.value)}
                      className="w-full"
                    />
                  </Formik>
                ) : rowData.updateDate ? (
                  new Date(rowData.updateDate).toLocaleDateString("bg-BG")
                ) : (
                  ""
                )
              }
            />
            <Column
              field="modifiedByUsername"
              header="Въведено от"
              style={{ width: "120px" }}
              body={(rowData) => (rowData.modifiedByUsername ? rowData.modifiedByUsername : "")}
            />
            <Column
              body={(rowData) => (
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  onClick={() => handleDelete(rowData.semester, rowData.subjectId)}
                />
              )}
              style={{ width: "80px" }}
            />
          </DataTable>
        ) : (
          ""
        )}
        <ConfirmDialog />
      </Card>
    </>
  );
};

export default ProtocolGradeSubjectsList;
